// Formatting and date helpers. Dates are 'YYYY-MM-DD' strings; wall-clock times 'YYYY-MM-DDTHH:MM'.

export const LOCALE = 'en-US';
export const TZ_DEFAULT = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Vancouver';
export const TIMEZONES: string[] = (() => {
  try {
    return (Intl as unknown as { supportedValuesOf: (k: string) => string[] }).supportedValuesOf('timeZone');
  } catch {
    return [TZ_DEFAULT, 'UTC'];
  }
})();
export const COLORS = ['#0f766e', '#2563eb', '#7c3aed', '#db2777', '#dc2626', '#ea580c', '#ca8a04', '#16a34a', '#475569'];

export const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

const utc = (d: string) => new Date(`${d.slice(0, 10)}T00:00:00Z`);
export const fmtDay = (d: string, opts: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric' }) => utc(d).toLocaleDateString(LOCALE, { timeZone: 'UTC', ...opts });
export function addDays(date: string, n: number) {
  const d = utc(date);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Today's date in a time zone (not UTC, which is a day off for half the world). */
export function todayIn(tz: string) {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  } catch {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
}

/** Wall-clock arithmetic (treated as UTC so DST never shifts it). */
export const wallMs = (v: string) => (v ? Date.parse(`${v.length === 10 ? `${v}T00:00` : v.slice(0, 16)}:00Z`) : NaN);
export const wallStr = (ms: number, dateOnly = false) => new Date(ms).toISOString().slice(0, dateOnly ? 10 : 16);

export function ago(ms: number | null | undefined) {
  if (!ms) return '';
  const s = (Date.now() - ms) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)} d ago`;
  return new Date(ms).toLocaleDateString(LOCALE, { month: 'short', day: 'numeric' });
}

export function clock(min: number, short = false) {
  const h24 = Math.floor(min / 60) % 24;
  const m = min % 60;
  const h12 = h24 % 12 || 12;
  const ap = h24 < 12 ? 'am' : 'pm';
  if (short) return m ? `${h12}:${String(m).padStart(2, '0')}${ap[0]}` : `${h12}${ap[0]}`;
  return `${h12}:${String(m).padStart(2, '0')} ${ap.toUpperCase()}`;
}
export function clockRange(a: number, b: number) {
  const s = clock(a);
  const e = clock(b % 1440);
  return s.slice(-2) === e.slice(-2) ? `${s.slice(0, -3)}–${e}` : `${s} – ${e}`;
}
export const minutesOf = (v: string) => Number(v.slice(11, 13)) * 60 + Number(v.slice(14, 16));

const DAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export function describeRrule(r: string) {
  if (!r) return '';
  const p = Object.fromEntries(r.split(';').map((x) => x.split('='))) as Record<string, string>;
  const n = Number(p.INTERVAL || 1);
  const unit = ({ DAILY: 'day', WEEKLY: 'week', MONTHLY: 'month', YEARLY: 'year' } as Record<string, string>)[p.FREQ] || '';
  let s = n > 1 ? `Every ${n} ${unit}s` : ({ DAILY: 'Daily', WEEKLY: 'Weekly', MONTHLY: 'Monthly', YEARLY: 'Yearly' } as Record<string, string>)[p.FREQ] || 'Repeats';
  if (p.BYDAY) {
    const days = p.BYDAY.split(',').map((d) => d.slice(-2));
    s = days.join(',') === 'MO,TU,WE,TH,FR' ? 'Every weekday' : `${s} on ${days.map((d) => DAY_NAMES[DAY_CODES.indexOf(d)]).join(', ')}`;
  }
  if (p.COUNT) s += `, ${p.COUNT} times`;
  if (p.UNTIL) s += `, until ${fmtDay(`${p.UNTIL.slice(0, 4)}-${p.UNTIL.slice(4, 6)}-${p.UNTIL.slice(6, 8)}`, { month: 'short', day: 'numeric', year: 'numeric' })}`;
  return s;
}

/** How an event's time reads in a card: "Thu, Oct 15 · 9:00–10:00 AM", "Oct 5 – Oct 7". */
export function fmtWhen(e: { all_day: boolean; start: string; end: string }) {
  if (e.all_day) {
    const last = addDays(e.end.slice(0, 10), -1);
    return last > e.start.slice(0, 10) ? `${fmtDay(e.start)} – ${fmtDay(last)}` : `${fmtDay(e.start)} · All day`;
  }
  if (e.start.endsWith('Z')) return new Date(e.start).toLocaleString(LOCALE, { dateStyle: 'medium', timeStyle: 'short' });
  const s = minutesOf(e.start);
  const en = minutesOf(e.end);
  return e.end.slice(0, 10) === e.start.slice(0, 10) ? `${fmtDay(e.start)} · ${clockRange(s, en)}` : `${fmtDay(e.start)}, ${clock(s)} – ${fmtDay(e.end)}, ${clock(en)}`;
}

export const VISIBILITY = {
  private: { label: 'Private', desc: 'Only you and people you add can see it. Can’t be subscribed to — good for drafts.' },
  unlisted: { label: 'Link only', desc: 'Anyone with the link can subscribe. Not searchable — good for classes and teams.' },
  public: { label: 'Public', desc: 'Listed in the public directory so others can include it — good for holidays and school-wide events.' },
} as const;

export const ROLE_LABEL = { owner: 'Owner', editor: 'Editor', viewer: 'Viewer' } as const;
